import React from 'react'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import MaterialTableCustom from '../../../components/MaterialTableCustom'
import { ExportCsv } from '@material-table/exporters'
import clsx from 'clsx'

const useStyles = makeStyles(() => ({
  tablePaper: {
    fontFamily: 'Nunito',
    marginTop: 20,
    borderRadius: 20,
    paddingLeft: 0,
    paddingRight: 20,
    paddingBottom: 20,
    overflow: 'hidden',
    '& .MuiPaper-elevation2': {
      overflow: 'hidden',
      boxShadow: 'unset',
    },
    '& .MuiToolbar-gutters': {
      minHeight: 50,
      marginLeft: 10,
      marginTop: 10,
    },
  },
}))

// pastel background colors (lighter shades blend better with white theme)
const riskColors = {
  High: '#f8d7da', // light red (danger)
  Medium: '#fff3cd', // light orange (warning)
  Low: '#d4edda', // light green (success)
}
// fixed column width for risk columns to keep Low/Medium/High uniform
const riskColWidth = 80

const PartRisksTable = () => {
  const classes = useStyles()

  const dummyData = [
    {
      partNumber: '569-0014',
      description: 'CONN CAP',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Medium',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'High',
      price: 'Low',
    },
    {
      partNumber: '569-0015',
      description: 'GROUND WIRE',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Medium',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0036',
      description: 'CLAMP',
      supplier: 'Low',
      supply: 'Medium',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0034',
      description: 'ROUTER',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Medium',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0028',
      description: 'INPUT PORT O-RING',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Medium',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0038',
      description: 'SPRING CLAMP',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'High',
    },
    {
      partNumber: '569-0035',
      description: 'INPUT PORT',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Medium',
      price: 'Low',
    },
    {
      partNumber: '569-0033',
      description: 'GASKET',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0032',
      description: 'WIRE SLEEVE',
      supplier: 'Low',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
    {
      partNumber: '569-0027',
      description: 'WIRE HARNESS',
      supplier: 'Medium',
      supply: 'Low',
      quality: 'Low',
      regulatory: 'Low',
      shelfLife: 'Low',
      alternatives: 'Low',
      eol: 'Low',
      price: 'Low',
    },
  ]

  const columns = [
    { title: 'Part Number', field: 'partNumber' },
    { title: 'Description', field: 'description' },
    {
      title: 'Supplier',
      field: 'supplier',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.supplier],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.supplier}
        </div>
      ),
    },
    {
      title: 'Supply',
      field: 'supply',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.supply],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.supply}
        </div>
      ),
    },
    {
      title: 'Quality',
      field: 'quality',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.quality],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.quality}
        </div>
      ),
    },
    {
      title: 'Regulatory',
      field: 'regulatory',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.regulatory],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.regulatory}
        </div>
      ),
    },
    {
      title: 'Shelf Life',
      field: 'shelfLife',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.shelfLife],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.shelfLife}
        </div>
      ),
    },
    {
      title: 'Alternatives',
      field: 'alternatives',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.alternatives],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.alternatives}
        </div>
      ),
    },
    {
      title: 'EOL',
      field: 'eol',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.eol],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.eol}
        </div>
      ),
    },
    {
      title: 'Price',
      field: 'price',
      headerStyle: { minWidth: riskColWidth, maxWidth: riskColWidth },
      cellStyle: { padding: 0 },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: riskColors[rowData.price],
            color: 'black',
            width: '100%',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {rowData.price}
        </div>
      ),
    },
  ]

  return (
    <Paper className={clsx('mat-table-cont', classes.tablePaper)}>
      <MaterialTableCustom
        title='Supply Chain Risk Analysis'
        columns={columns}
        data={dummyData}
        headerStyle={{
          marginTop: -5,
          paddingTop: 0,
          paddingBottom: 5,
        }}
        rowStyle={{
          fontSize: `12px`,
          textAlign: 'center',
        }}
        options={{
          search: true,
          paging: false,
        }}
      />
    </Paper>
  )
}

export default PartRisksTable
